import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import StoryCard from "@/components/feature/StoryCard";
import SearchInput from "@/components/base/SearchInput";
import ChipGroup from "@/components/base/ChipGroup";
import SelectField from "@/components/base/SelectField";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { storyList, storySeasons } from "@/utils/catalog";

const PAGE_SIZE = 12;

const sortOptions = [
  { label: "最新更新", value: "newest" },
  { label: "最早更新", value: "oldest" },
  { label: "按集数排列", value: "episode" },
];

export default function StoryCategoryPage() {
  const [keyword, setKeyword] = useState("");
  const [season, setSeason] = useState("全部");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = storyList.filter((story) => {
      const matchSeason = season === "全部" || story.season === season;
      const matchKeyword =
        !q ||
        story.title.toLowerCase().includes(q) ||
        story.summary.toLowerCase().includes(q) ||
        story.tags.join("").toLowerCase().includes(q);
      return matchSeason && matchKeyword;
    });
    const sorted = [...list];
    if (sort === "newest") sorted.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    if (sort === "oldest") sorted.sort((a, b) => a.publishedAt.localeCompare(b.publishedAt));
    if (sort === "episode") sorted.sort((a, b) => a.episode - b.episode);
    return sorted;
  }, [keyword, season, sort]);

  useEffect(() => {
    setPage(1);
  }, [keyword, season, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setKeyword("");
    setSeason("全部");
    setSort("newest");
  };

  return (
    <PageShell
      active="story"
      crumbs={[{ label: "首页", to: "/" }, { label: "剧情介绍" }]}
    >
      <header className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-semibold text-foreground-900">年轻的母亲-剧情介绍</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          按季与集数完整梳理“年轻的母亲”剧情脉络，收录分集梗概与关键转折，帮助你快速了解每一集发生了什么。
        </p>
      </header>

      <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput value={keyword} onChange={setKeyword} placeholder="搜索剧情标题或关键词…" className="sm:max-w-xs" />
          <SelectField label="排序" value={sort} onChange={setSort} options={sortOptions} />
        </div>
        <ChipGroup label="剧情章节" options={storySeasons} value={season} onChange={setSeason} />
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
        <span>
          共找到 <span className="text-primary-600">{filtered.length}</span> 条剧情
        </span>
        {(keyword || season !== "全部") && (
          <button type="button" onClick={resetFilters} className="transition hover:text-primary-600">
            <i className="ri-refresh-line mr-1"></i>清除筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 md:gap-5 xl:grid-cols-3">
          {pageItems.map((story) => (
            <StoryCard key={story.id} story={story} />
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState
            title="暂无符合条件的剧情"
            description="尝试清除筛选条件，或换一个关键词再搜索看看。"
            actionText="清除筛选"
            onAction={resetFilters}
          />
        </div>
      )}

      <div className="mt-8">
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </PageShell>
  );
}